import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SafeIcon from "../common/SafeIcon";
import { FiActivity, FiMenu } from "react-icons/fi";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    if (location.pathname !== "/") return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { root: null, rootMargin: "-20% 0px -70% 0px", threshold: 0 },
    );

    ["home", "pricing", "contact"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [location.pathname]);

  if (location.pathname.startsWith("/admin")) return null;

  const handleScroll = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { name: "Home", id: "home", path: "/", action: () => handleScroll("home") },
    { name: "News", id: "news", path: "/news" },
    {
      name: "Pricing",
      id: "pricing",
      path: "/#pricing",
      action: () => handleScroll("pricing"),
    },
    {
      name: "Contact",
      id: "contact",
      path: "/#contact",
      action: () => handleScroll("contact"),
    },
  ];

  const isActive = (item) => {
    if (item.id === "news") return location.pathname === "/news";
    if (location.pathname === "/" || location.pathname === "")
      return activeSection === item.id;
    return false;
  };

  return (
    <header className="fixed top-0 left-0 w-full z-[90] bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
        <Link
          to="/"
          onClick={() => handleScroll("home")}
          className="flex items-center gap-4 group"
        >
          <div className="w-10 h-10 border border-[#CCFF00] flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[#CCFF00] opacity-10 group-hover:opacity-20 transition-opacity" />
            <SafeIcon
              icon={FiActivity}
              className="text-[#CCFF00] text-xl group-hover:scale-110 transition-transform"
            />
          </div>
          <div className="flex flex-col">
            <span className="font-black text-xl tracking-tighter text-white uppercase leading-none group-hover:text-[#CCFF00] transition-colors">
              The Pitch Feed
            </span>
            <span className="text-[8px] font-mono-data text-slate-500 uppercase tracking-[0.3em] mt-1">
              Global football agency
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.name}
              type="button"
              onClick={item.action || (() => navigate(item.path))}
              className={`text-[10px] font-black uppercase tracking-[0.3em] transition-all relative py-2 ${
                isActive(item)
                  ? "text-[#CCFF00]"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {item.name}
              {isActive(item) && (
                <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[#CCFF00]" />
              )}
            </button>
          ))}
        </nav>

        <div className="md:hidden w-10 h-10 bg-white/5 flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors">
          <SafeIcon icon={FiMenu} className="text-white text-xl" />
        </div>
      </div>
    </header>
  );
};

export default Navbar;